import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../services/apiConfig';

export function SSIWidget() {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const scriptInjectedRef = useRef(false);
  const lastLocationRef = useRef(location.pathname);

  // Inject script once
  useEffect(() => {
    if (!API_CONFIG.ssiSupabaseUrl || !API_CONFIG.ssiSupabaseKey) {
      console.warn('SSI Widget: Missing configuration');
      return;
    }

    if (loading) return;

    // Get user identifier - prioritize candidateId, then mobileNumber, then email
    const userId = currentUser?.candidateId?.toString() || 
                   currentUser?.mobileNumber || 
                   currentUser?.email || 
                   '';
    
    console.log('[SSI Widget] User info:', { 
      candidateId: currentUser?.candidateId, 
      mobileNumber: currentUser?.mobileNumber,
      email: currentUser?.email,
      selectedUserId: userId 
    });
    
    // Remove any duplicate widget hosts first
    const existingHosts = document.querySelectorAll('#ssi-widget-host');
    if (existingHosts.length > 1) {
      console.log('[SSI Widget] Found duplicate hosts, cleaning up...');
      existingHosts.forEach((host, index) => {
        if (index > 0) host.remove();
      });
    }
    
    // Check if script already exists
    const existingScript = document.getElementById('ssi-widget');
    if (existingScript) {
      const currentAttr = existingScript.getAttribute('data-user-id');
      if (currentAttr !== userId) {
        console.log('[SSI Widget] Updating user ID from', currentAttr, 'to', userId);
        existingScript.setAttribute('data-user-id', userId);
      }
      return;
    }

    if (scriptInjectedRef.current) return;
    scriptInjectedRef.current = true;

    console.log('[SSI Widget] Injecting widget script with user ID:', userId);

    const widgetSrc = `${API_CONFIG.ssiSupabaseUrl}/storage/v1/object/public/static/ssi-widget.js`;

    const script = document.createElement('script');
    script.id = 'ssi-widget';
    script.src = widgetSrc;
    script.setAttribute('data-supabase-url', API_CONFIG.ssiSupabaseUrl);
    script.setAttribute('data-supabase-key', API_CONFIG.ssiSupabaseKey);
    if (userId) {
      script.setAttribute('data-user-id', userId);
    }
    script.async = true;

    document.body.appendChild(script);
  }, [currentUser, loading]);

  // Trigger widget state update on route change
  useEffect(() => {
    if (location.pathname !== lastLocationRef.current) {
      lastLocationRef.current = location.pathname;
      
      // Wait for widget to be loaded, then trigger popstate event
      // The widget listens to popstate for SPA navigation
      setTimeout(() => {
        const event = new PopStateEvent('popstate', { state: null });
        window.dispatchEvent(event);
        console.log('🔄 SSI Widget: Route changed to', location.pathname, '- triggered widget update');
      }, 100);
    }
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const script = document.getElementById('ssi-widget');
      if (script) script.remove();
      const host = document.getElementById('ssi-widget-host');
      if (host) host.remove();
      scriptInjectedRef.current = false;
    };
  }, []);

  return null;
}
